import { useEffect, useState } from "react";

interface AvatarProps {
  /** Uploaded photo URL (from usersApi.avatarUrl). Null → generated. */
  src?: string | null;
  /** Wallet address — seeds the deterministic generated gradient. */
  address?: string | null;
  /** Display name — its first letter is shown on the generated avatar. */
  handle?: string | null;
  /** Pixel size of the square. Default 40. */
  size?: number;
  /** Extra classes (e.g. ring / border). */
  className?: string;
}

/** Cheap stable string hash → 32-bit unsigned. */
function hash(str: string): number {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Avatar with a deterministic generated fallback.
 *
 * When `src` is present we render the uploaded photo; if it fails to load
 * (404 / network) we silently fall back to a gradient derived from the
 * wallet address plus the handle's initial — so every player always has a
 * recognisable, stable avatar even before they upload one. Used in the
 * header, profile dropdown, settings page and profile page.
 */
export default function Avatar({ src, address, handle, size = 40, className = "" }: AvatarProps) {
  const [failed, setFailed] = useState(false);
  // Reset the error flag whenever the source changes (new upload / version).
  useEffect(() => setFailed(false), [src]);

  const seed = (address ?? handle ?? "hupper").toLowerCase();
  const h = hash(seed);
  const hue = h % 360;
  const hue2 = (hue + 48) % 360;
  const initial =
    handle?.trim()?.[0]?.toUpperCase() ??
    address?.replace(/^0x/i, "")?.[0]?.toUpperCase() ??
    "?";

  const dims = { width: size, height: size };

  if (src && !failed) {
    return (
      <img
        src={src}
        alt=""
        draggable={false}
        onError={() => setFailed(true)}
        style={dims}
        className={`rounded-full object-cover shrink-0 ${className}`}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      style={{
        ...dims,
        backgroundImage: `linear-gradient(135deg, hsl(${hue} 70% 52%), hsl(${hue2} 72% 42%))`,
        fontSize: Math.round(size * 0.42),
      }}
      className={`rounded-full shrink-0 inline-flex items-center justify-center font-semibold text-white/95 select-none ${className}`}
    >
      {initial}
    </span>
  );
}
